import React, { useState } from 'react';
import { Plus, ChevronDown, Check, Shield, DoorOpen, ClipboardList, AlertCircle, Lightbulb, DollarSign, CheckCircle2, Phone } from 'lucide-react';
import { CallDetailsModal } from './CallDetailsModal';
interface CallRecord {
  id: string;
  talkTime: string;
  callScore: number;
  timestamp: Date;
  leadType: string;
  leadSource: string;
  industry: string;
}
interface CallHistoryListProps {
  calls: CallRecord[];
  onViewDetails: (callId: string) => void;
}
type FilterOption = 'most-recent' | 'highest-rated' | 'longest-time' | 'shortest-time' | 'fastest-open' | 'fastest-close' | 'least-words-close' | 'most-words-close' | 'call-score';
const filterOptions: {
  value: FilterOption;
  label: string;
}[] = [{
  value: 'most-recent',
  label: 'Most Recent'
}, {
  value: 'highest-rated',
  label: 'Highest Rated'
}, {
  value: 'longest-time',
  label: 'Longest Call Time'
}, {
  value: 'shortest-time',
  label: 'Shortest Call Time'
}, {
  value: 'fastest-open',
  label: 'Fastest to Open'
}, {
  value: 'fastest-close',
  label: 'Fastest to Close'
}, {
  value: 'least-words-close',
  label: 'Least Words to Close'
}, {
  value: 'most-words-close',
  label: 'Most Words to Close'
}, {
  value: 'call-score',
  label: 'Call Score'
}];
function timeToSeconds(time: string): number {
  const [minutes, seconds] = time.split(':').map(Number);
  return minutes * 60 + seconds;
}
// Helper function to get text color based on score out of 10
function getScoreTextColor(score: number): string {
  const scoreOutOf10 = score / 10 * 10; // Normalize to 0-10 scale
  if (scoreOutOf10 >= 9) {
    return 'text-emerald-500';
  } else if (scoreOutOf10 >= 8) {
    return 'text-green-500';
  } else if (scoreOutOf10 >= 7) {
    return 'text-lime-500';
  } else if (scoreOutOf10 >= 6) {
    return 'text-yellow-500';
  } else if (scoreOutOf10 >= 5) {
    return 'text-amber-500';
  } else if (scoreOutOf10 >= 4) {
    return 'text-orange-500';
  } else if (scoreOutOf10 >= 3) {
    return 'text-red-500';
  } else {
    return 'text-rose-500';
  }
}
// Helper function to calculate milestone progress based on call score (7 stages)
function getMilestoneProgress(callScore: number): {
  gate: number;
  open: number;
  eval: number;
  pain: number;
  solution: number;
  value: number;
  close: number;
} {
  // Normalize score to 0-100 range
  const normalizedScore = callScore / 10 * 100;
  // Progressive milestone completion based on score (7 stages)
  // Each stage represents ~14.3% of the journey
  if (normalizedScore >= 95) {
    return {
      gate: 100,
      open: 100,
      eval: 100,
      pain: 100,
      solution: 100,
      value: 100,
      close: 100
    };
  } else if (normalizedScore >= 80) {
    return {
      gate: 100,
      open: 100,
      eval: 100,
      pain: 100,
      solution: 100,
      value: 100,
      close: Math.min(100, (normalizedScore - 80) * 6.67)
    };
  } else if (normalizedScore >= 65) {
    return {
      gate: 100,
      open: 100,
      eval: 100,
      pain: 100,
      solution: 100,
      value: Math.min(100, (normalizedScore - 65) * 6.67),
      close: 0
    };
  } else if (normalizedScore >= 50) {
    return {
      gate: 100,
      open: 100,
      eval: 100,
      pain: 100,
      solution: Math.min(100, (normalizedScore - 50) * 6.67),
      value: 0,
      close: 0
    };
  } else if (normalizedScore >= 35) {
    return {
      gate: 100,
      open: 100,
      eval: 100,
      pain: Math.min(100, (normalizedScore - 35) * 6.67),
      solution: 0,
      value: 0,
      close: 0
    };
  } else if (normalizedScore >= 20) {
    return {
      gate: 100,
      open: 100,
      eval: Math.min(100, (normalizedScore - 20) * 6.67),
      pain: 0,
      solution: 0,
      value: 0,
      close: 0
    };
  } else if (normalizedScore >= 10) {
    return {
      gate: 100,
      open: Math.min(100, (normalizedScore - 10) * 10),
      eval: 0,
      pain: 0,
      solution: 0,
      value: 0,
      close: 0
    };
  } else {
    return {
      gate: Math.min(100, normalizedScore * 10),
      open: 0,
      eval: 0,
      pain: 0,
      solution: 0,
      value: 0,
      close: 0
    };
  }
}
// Helper function to get milestone color based on percentage
function getMilestoneColor(percentage: number): string {
  if (percentage === 100) {
    return 'bg-gradient-to-br from-green-400 to-green-500';
  } else if (percentage >= 75) {
    return 'bg-gradient-to-br from-lime-400 to-lime-500';
  } else if (percentage >= 50) {
    return 'bg-gradient-to-br from-yellow-300 to-yellow-400';
  } else if (percentage >= 25) {
    return 'bg-gradient-to-br from-orange-400 to-orange-500';
  } else if (percentage > 0) {
    return 'bg-gradient-to-br from-red-400 to-red-500';
  } else {
    return 'bg-gray-200';
  }
}
interface MilestoneBadgeProps {
  icon: React.ReactNode;
  label: string;
  percentage: number;
}
function MilestoneBadge({
  icon,
  label,
  percentage
}: MilestoneBadgeProps) {
  const colorClass = getMilestoneColor(percentage);
  const isActive = percentage > 0;
  return <div className="flex flex-col items-center gap-1" data-id="element-264">
      <div className={`w-8 h-8 ${colorClass} rounded-[12px] flex items-center justify-center shadow-sm transition-all ${isActive ? 'text-white' : 'text-gray-400'}`} data-id="element-265">
        <div className="w-3.5 h-3.5 flex items-center justify-center" data-id="element-266">
          {icon}
        </div>
      </div>
      <span className="text-[8px] font-medium text-gray-500 text-center leading-tight max-w-[36px]" data-id="element-267">
        {label}
      </span>
    </div>;
}
export function CallHistoryList({
  calls,
  onViewDetails
}: CallHistoryListProps) {
  const [selectedCall, setSelectedCall] = useState<CallRecord | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<FilterOption>('most-recent');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const handleViewDetails = (call: CallRecord) => {
    setSelectedCall(call);
    onViewDetails(call.id);
  };
  const sortedCalls = [...calls].sort((a, b) => {
    switch (selectedFilter) {
      case 'most-recent':
        return b.timestamp.getTime() - a.timestamp.getTime();
      case 'highest-rated':
        return b.callScore - a.callScore;
      case 'longest-time':
        return timeToSeconds(b.talkTime) - timeToSeconds(a.talkTime);
      case 'shortest-time':
        return timeToSeconds(a.talkTime) - timeToSeconds(b.talkTime);
      case 'call-score':
        return b.callScore - a.callScore;
      // For demo purposes, using callScore as proxy for other metrics
      case 'fastest-open':
      case 'fastest-close':
      case 'least-words-close':
        return a.callScore - b.callScore;
      case 'most-words-close':
        return b.callScore - a.callScore;
      default:
        return 0;
    }
  });
  if (calls.length === 0) {
    return <div className="px-6 py-12 text-center" data-id="element-268">
        <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-269">
          <div className="text-2xl font-bold text-gray-400" data-id="element-270">0</div>
        </div>
        <p className="text-gray-500 font-medium" data-id="element-271">No calls yet</p>
        <p className="text-sm text-gray-400 mt-1" data-id="element-272">
          Start your first training call above
        </p>
      </div>;
  }
  const selectedFilterLabel = filterOptions.find(opt => opt.value === selectedFilter)?.label || 'Most Recent';
  return <>
      <div className="px-6 pb-6 space-y-3" data-id="element-273">
        <div className="flex items-center justify-between mb-4" data-id="element-274">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide" data-id="element-275">
            Recent Calls
          </h3>

          {/* Filter Dropdown */}
          <div className="relative" data-id="element-276">
            <button onClick={() => setShowFilterDropdown(!showFilterDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors text-xs font-medium text-gray-700" data-id="element-277">
              <span data-id="element-278">{selectedFilterLabel}</span>
              <ChevronDown className="w-3.5 h-3.5 text-gray-400" data-id="element-279" />
            </button>

            {showFilterDropdown && <>
                {/* Backdrop to close dropdown */}
                <div className="fixed inset-0 z-10" onClick={() => setShowFilterDropdown(false)} data-id="element-280" />

                {/* Dropdown Menu */}
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-20 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-281">
                  {filterOptions.map(option => <button key={option.value} onClick={() => {
                setSelectedFilter(option.value);
                setShowFilterDropdown(false);
              }} className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors flex items-center justify-between" data-id="element-282">
                      <span className={selectedFilter === option.value ? 'font-semibold text-gray-900' : 'text-gray-700'} data-id="element-283">
                        {option.label}
                      </span>
                      {selectedFilter === option.value && <Check className="w-4 h-4 text-blue-600" data-id="element-284" />}
                    </button>)}
                </div>
              </>}
          </div>
        </div>

        {sortedCalls.map(call => {
        const scoreOutOf10 = call.callScore.toFixed(1);
        const textColor = getScoreTextColor(call.callScore);
        const milestones = getMilestoneProgress(call.callScore);
        return <div key={call.id} className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 shadow-sm" data-id="element-285">
              <div className="flex items-start gap-4 mb-2" data-id="element-286">
                {/* Smaller Phone Icon Box */}
                <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-[16px] flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-287">
                  <Phone className="w-5 h-5 text-gray-600" data-id="element-288" />
                </div>

                <div className="flex-1 min-w-0" data-id="element-289">
                  <div className="flex items-start gap-6" data-id="element-290">
                    <div data-id="element-291">
                      <p className="text-xs text-gray-500 font-medium mb-0.5" data-id="element-292">
                        Talk Time
                      </p>
                      <p className="text-2xl font-bold text-gray-900 leading-tight" data-id="element-293">
                        {call.talkTime}
                      </p>
                    </div>

                    <div data-id="element-294">
                      <p className="text-xs text-gray-500 font-medium mb-0.5" data-id="element-295">
                        Call Score
                      </p>
                      <p className={`text-2xl font-bold ${textColor} leading-tight`} data-id="element-296">
                        {scoreOutOf10}
                      </p>
                    </div>
                  </div>
                </div>

                <button onClick={() => handleViewDetails(call)} className="w-10 h-10 bg-black rounded-full flex items-center justify-center text-white shadow-lg hover:bg-gray-800 transition-colors flex-shrink-0" aria-label="View call details" data-id="element-297">
                  <Plus className="w-5 h-5" data-id="element-298" />
                </button>
              </div>

              {/* Metadata Row - Full Width */}
              <div className="flex items-center gap-2 text-xs text-gray-400 mb-4 px-1" data-id="element-299">
                <span data-id="element-300">{call.leadType}</span>
                <span data-id="element-301">•</span>
                <span data-id="element-302">{call.leadSource}</span>
                <span data-id="element-303">•</span>
                <span data-id="element-304">{call.industry}</span>
              </div>

              {/* Pitch Progress Section */}
              <div data-id="element-305">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2 text-center" data-id="element-306">
                  Pitch Progress
                </p>
                <div className="flex justify-center gap-2" data-id="element-307">
                  <MilestoneBadge icon={<Shield className="w-3.5 h-3.5" data-id="element-309" />} label="Gate" percentage={milestones.gate} data-id="element-308" />
                  <MilestoneBadge icon={<DoorOpen className="w-3.5 h-3.5" data-id="element-311" />} label="Open" percentage={milestones.open} data-id="element-310" />
                  <MilestoneBadge icon={<ClipboardList className="w-3.5 h-3.5" data-id="element-313" />} label="Eval" percentage={milestones.eval} data-id="element-312" />
                  <MilestoneBadge icon={<AlertCircle className="w-3.5 h-3.5" data-id="element-315" />} label="Pain" percentage={milestones.pain} data-id="element-314" />
                  <MilestoneBadge icon={<Lightbulb className="w-3.5 h-3.5" data-id="element-317" />} label="Solution" percentage={milestones.solution} data-id="element-316" />
                  <MilestoneBadge icon={<DollarSign className="w-3.5 h-3.5" data-id="element-319" />} label="Value" percentage={milestones.value} data-id="element-318" />
                  <MilestoneBadge icon={<CheckCircle2 className="w-3.5 h-3.5" data-id="element-321" />} label="Close" percentage={milestones.close} data-id="element-320" />
                </div>
              </div>
            </div>;
      })}
      </div>

      <CallDetailsModal isOpen={selectedCall !== null} onClose={() => setSelectedCall(null)} callData={selectedCall || calls[0]} data-id="element-322" />
    </>;
}